import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, FileText, Eye, EyeOff, Image as ImageIcon, Inbox, Calendar } from "lucide-react";
import { useContent } from "@/contexts/ContentContext";
import { api } from "@/lib/api";

const SECTIONS = [
  { key: "hero", label: "Hero", color: "from-amber-500/15" },
  { key: "services", label: "Services", color: "from-amber-500/15" },
  { key: "about", label: "About", color: "from-amber-500/15" },
  { key: "portfolio", label: "Portfolio", color: "from-amber-500/15" },
  { key: "process", label: "How It Works", color: "from-amber-500/15" },
  { key: "testimonials", label: "Testimonials", color: "from-amber-500/15" },
  { key: "areas", label: "Service Areas", color: "from-amber-500/15" },
  { key: "cta", label: "CTA Banner", color: "from-amber-500/15" },
  { key: "contact", label: "Contact", color: "from-amber-500/15" },
  { key: "footer", label: "Footer", color: "from-amber-500/15" },
];

export default function Dashboard() {
  const { content, loading } = useContent();
  const [quoteCount, setQuoteCount] = useState(null);

  useEffect(() => {
    api.get("/quote").then((r) => setQuoteCount(r.data.length)).catch(() => setQuoteCount(0));
  }, []);

  if (loading || !content) {
    return <div className="p-10 text-white/60">Loading…</div>;
  }

  const sections = content.sections;
  const enabledCount = SECTIONS.filter((s) => sections[s.key]?.enabled !== false).length;

  return (
    <div className="p-6 sm:p-10 max-w-6xl">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] font-semibold mb-2">Dashboard</p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">
            Welcome back, <span className="text-gradient-gold">Admin</span>
          </h1>
          <p className="mt-2 text-white/60 text-sm sm:text-base">Manage every section, image, and word on your site.</p>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <Stat icon={Inbox} label="Quote Requests" value={quoteCount ?? "—"} to="/admin/cms/quotes" />
        <Stat icon={Eye} label="Visible Sections" value={`${enabledCount} / ${SECTIONS.length}`} />
        <Stat icon={ImageIcon} label="Portfolio Items" value={sections.portfolio?.items?.length || 0} to="/admin/cms/section/portfolio" />
        <Stat icon={Calendar} label="Services" value={sections.services?.items?.length || 0} to="/admin/cms/section/services" />
      </div>

      <h2 className="font-display text-xl font-bold mb-5 flex items-center gap-2">
        <FileText className="w-5 h-5 text-[#D4AF37]" /> Page sections
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {SECTIONS.map((s) => {
          const sec = sections[s.key] || {};
          const enabled = sec.enabled !== false;
          return (
            <Link
              key={s.key}
              to={`/admin/cms/section/${s.key}`}
              data-testid={`dashboard-section-${s.key}`}
              className="group bg-white/[0.04] border border-white/10 rounded-2xl p-5 hover:border-[#D4AF37]/50 transition-all flex items-start justify-between"
            >
              <div>
                <div className="flex items-center gap-2 mb-1">
                  {enabled ? (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-400 font-bold">
                      <Eye className="w-3 h-3" /> Visible
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-white/40 font-bold">
                      <EyeOff className="w-3 h-3" /> Hidden
                    </span>
                  )}
                </div>
                <div className="font-display text-lg font-bold">{s.label}</div>
                <div className="text-xs text-white/50 mt-1 truncate">
                  {sec.title || sec.headline_1 || sec.eyebrow || "Edit content…"}
                </div>
              </div>
              <ArrowUpRight className="w-5 h-5 text-white/30 group-hover:text-[#D4AF37] group-hover:rotate-12 transition-all" />
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, to }) {
  const inner = (
    <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-5 hover:border-[#D4AF37]/40 transition-all h-full">
      <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center mb-4">
        <Icon className="w-4 h-4 text-[#D4AF37]" />
      </div>
      <div className="font-display text-2xl font-bold">{value}</div>
      <div className="text-xs text-white/50 uppercase tracking-wider mt-1">{label}</div>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}
